/**
 * Player preferences, persisted in localStorage. Vanilla store so the renderer and the audio
 * module can read it outside React (`settingsStore.getState().showGrid`).
 */
import { useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { detectLocale, type Locale, setLocale } from '../i18n/locale';

/**
 * How units are drawn on the map and in the HUD: side-view silhouettes on cardboard counters,
 * or the staff's NATO APP-6 symbols (docs/Rendering.md, "Counter styles").
 */
export type UnitStyle = 'illustrated' | 'nato';
export const UNIT_STYLES: readonly UnitStyle[] = ['illustrated', 'nato'];

export interface Settings {
  /** Master volume, 0..1 (scales ambience and effects). */
  volume: number;
  /** Ambience bed (room tone, wind, distant guns, dawn bell), 0..1 of master. */
  ambienceVolume: number;
  /** Interface and signal effects (clicks, stamps, morse, static), 0..1 of master. */
  effectsVolume: number;
  /** Silence everything; the audio context is suspended while muted. */
  muted: boolean;
  /** Show the Morning Dispatch newspaper at 06:00 each in-game day. */
  newspaper: boolean;
  /** HUD scale multiplier on top of the automatic resolution scale, 0.8..1.3. */
  uiScale: number;
  /** Pan the map when the pointer touches the window edge. */
  edgeScroll: boolean;
  /** Draw the hex grid over the map. */
  showGrid: boolean;
  /** Unit counter artwork; switches live on the map and in the HUD. */
  unitStyle: UnitStyle;
  /** Disable non-essential animation (also forced by the OS setting). */
  reducedMotion: boolean;
  /** Commander name used in multiplayer. */
  playerName: string;
  /** Interface language (docs/Localization.md); first run follows the browser. */
  language: Locale;
}

export interface SettingsState extends Settings {
  set(patch: Partial<Settings>): void;
  reset(): void;
}

export const DEFAULT_SETTINGS: Settings = {
  volume: 0.5,
  ambienceVolume: 0.6,
  effectsVolume: 0.8,
  muted: false,
  newspaper: true,
  uiScale: 1,
  edgeScroll: true,
  showGrid: false,
  unitStyle: 'illustrated',
  reducedMotion: false,
  playerName: '',
  language: detectLocale(globalThis.navigator?.language),
};

export const settingsStore = createStore<SettingsState>()(
  persist(
    (set, get) => ({
      ...DEFAULT_SETTINGS,
      set: (patch) => set(patch),
      // "Restore defaults" keeps who you are and the language you read.
      reset: () => set({ ...DEFAULT_SETTINGS, playerName: get().playerName, language: get().language }),
    }),
    {
      name: 'friction.settings',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: ({
        volume,
        ambienceVolume,
        effectsVolume,
        muted,
        newspaper,
        uiScale,
        edgeScroll,
        showGrid,
        unitStyle,
        reducedMotion,
        playerName,
        language,
      }) => ({
        volume,
        ambienceVolume,
        effectsVolume,
        muted,
        newspaper,
        uiScale,
        edgeScroll,
        showGrid,
        unitStyle,
        reducedMotion,
        playerName,
        language,
      }),
    },
  ),
);

/**
 * The language lives here (persisted); the i18n module holds the live copy that pure code reads.
 * Keep them in step — also after the persisted settings rehydrate — and tell the document.
 */
function applyLanguage(language: Locale): void {
  setLocale(language);
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}
applyLanguage(settingsStore.getState().language);
settingsStore.subscribe((s, prev) => {
  if (s.language !== prev.language) applyLanguage(s.language);
});

export function useSettings<T>(selector: (s: SettingsState) => T): T {
  return useStore(settingsStore, selector);
}

/** True when either the OS or the player asked for less motion. */
export function prefersReducedMotion(): boolean {
  return settingsStore.getState().reducedMotion || matchMedia('(prefers-reduced-motion: reduce)').matches;
}
